import { useMemo } from "react";

import { getAppShortName, getWalletProviderKind } from "../../config/chains";
import { ID_TO_CHAINS } from "../../hooks/wallet/useWallet";
import { supportedChainId } from "../../utils/wallet/connectors";
import { SDK_KIND_TO_WALLET_ENUM } from "../../utils/wallet/types";
import ConnectWallet from "./ConnectWallet";
import Deposit from "./Deposit";
import EnableTrading from "./EnableTrading";
import Onboard from "./Onboard";
import SwitchNetwork from "./SwitchNetwork";
import { IConnectWalletModalProps, ConnectWalletPageEnum } from "./types";
import { useModalSetup } from "./useModalSetup";
import { useModalPaging } from "./useModalPaging";
import { useModalEffects } from "./useModalEffects";
import { useModalActions } from "./useModalActions";

type Ctx = IConnectWalletModalProps &
  ReturnType<typeof useModalSetup> &
  ReturnType<typeof useModalPaging> &
  ReturnType<typeof useModalEffects> &
  ReturnType<typeof useModalActions>;

export function useModalView(ctx: Ctx) {
  const {
    page,
    t,
    referralCode,
    referralData,
    hasBrowserWallet,
    isConnectingToWallet,
    selectedWallet,
    selectedEIP6963Provider,
    setWalletAndConnect,
    connectedWallet,
    getImportedAccountAddress,
    selectedDepositChain,
    setSelectedDepositChain,
    isRememberMeChecked,
    setRememberMe,
    inviteCode,
    setInviteCode,
    inviteRequired,
    depositReady,
    showReadyToDepositMessage,
    showHowDepositsWork,
    setShowHowDepositsWork,
    depositForm,
    isLoading,
    setAssetToDeposit,
    assetToDeposit,
    depositError,
    depositAssetBalance,
    maxDepositBalance,
    maxDepositUnavailable,
    chainId,
    ethBalance,
    depositFees,
    yieldVaultToggle,
    setYieldVaultToggle,
    decimals,
    getAssetPermitSignature,
    loadingAllowance,
    availablePages,
  } = ctx;

  // On SDK chains (Privy/Para/DogeOS) the SDK's own modal is the wallet picker, so
  // the connect step just shows a brief "Connecting…" instead of "Select Your Wallet".
  const sdkWalletEnum = SDK_KIND_TO_WALLET_ENUM[getWalletProviderKind()];

  const currentPageTitle = useMemo(() => {
    switch (page) {
      case ConnectWalletPageEnum.ONBOARD:
        return t("welcome_to_app", { appName: getAppShortName() });
      case ConnectWalletPageEnum.CONNECT_WALLET:
        return sdkWalletEnum !== undefined
          ? t("connecting")
          : t("select_your_wallet");
      case ConnectWalletPageEnum.SWITCH_NETWORK:
        return t("switch_network");
      case ConnectWalletPageEnum.ENABLE_TRADING_SIGN:
        return t("complete_sign_in");
      case ConnectWalletPageEnum.DEPOSIT:
        return t("deposit");
      default:
        return "";
    }
  }, [page, t, sdkWalletEnum]);

  const pageContent = useMemo(() => {
    switch (page) {
      case ConnectWalletPageEnum.ONBOARD:
        return (
          <Onboard
            referralCode={referralCode}
            refereeDiscount={referralData?.referee_discount}
          />
        );
      case ConnectWalletPageEnum.CONNECT_WALLET:
        return (
          <ConnectWallet
            hasBrowserWallet={hasBrowserWallet}
            isConnectingToWallet={isConnectingToWallet}
            selectedWallet={selectedWallet}
            selectedEIP6963UUID={selectedEIP6963Provider?.info.uuid}
            setWallet={setWalletAndConnect}
            connectedWallet={connectedWallet}
            importedWalletAddress={getImportedAccountAddress()}
          />
        );
      case ConnectWalletPageEnum.SWITCH_NETWORK:
        return (
          <SwitchNetwork
            selectedNetwork={selectedDepositChain}
            onChangeSelectedNetwork={setSelectedDepositChain}
          />
        );
      case ConnectWalletPageEnum.ENABLE_TRADING_SIGN:
        return (
          <EnableTrading
            isRememberMeChecked={isRememberMeChecked}
            setRememberMe={setRememberMe}
            inviteCode={inviteCode}
            setInviteCode={setInviteCode}
            inviteRequired={inviteRequired}
          />
        );
      case ConnectWalletPageEnum.DEPOSIT:
        return (
          <Deposit
            selectedDepositNetwork={selectedDepositChain}
            showApprovalSuccessMessage={
              depositReady && showReadyToDepositMessage
            }
            showHelpInfo={showHowDepositsWork}
            onHideHelpInfo={() => setShowHowDepositsWork(false)}
            form={depositForm}
            isDepositing={isLoading}
            onChangeAsset={setAssetToDeposit}
            asset={assetToDeposit}
            customError={depositError}
            balance={
              depositAssetBalance
            }
            maxBalance={maxDepositBalance}
            maxUnavailableReason={maxDepositUnavailable}
            socketDepositInfo={
              chainId === selectedDepositChain &&
              selectedDepositChain !== supportedChainId
                ? {
                    ethBalance,
                    depositFee: depositFees,
                  }
                : undefined
            }
            onChangeSelectedDepositNetwork={setSelectedDepositChain}
            yieldVaultToggle={yieldVaultToggle}
            setYieldVaultToggle={setYieldVaultToggle}
          />
        );
      default:
        return null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    page,
    isConnectingToWallet,
    selectedWallet,
    connectedWallet,
    inviteCode,
    inviteRequired,
    isRememberMeChecked,
    depositReady,
    showHowDepositsWork,
    depositForm,
    isLoading,
    assetToDeposit,
    depositAssetBalance,
    decimals,
    depositForm.formState.isValid,
    selectedDepositChain,
    yieldVaultToggle,
  ]);

  const stepInfo = useMemo(() => {
    switch (page) {
      case ConnectWalletPageEnum.ONBOARD:
        return {
          prevHidden: true,
          nextLabel: t("continue"),
          nextDisabled: false,
        };
      case ConnectWalletPageEnum.CONNECT_WALLET:
        return {
          prevHidden: true,
          nextLabel: t("continue"),
          nextDisabled: !selectedWallet,
          nextHidden: isLoading || Boolean(isConnectingToWallet),
        };
      case ConnectWalletPageEnum.SWITCH_NETWORK:
        return {
          nextLabel: t("switch_to_chain", {
            chain: ID_TO_CHAINS[selectedDepositChain],
          }),
          nextDisabled: isLoading,
        };
      case ConnectWalletPageEnum.ENABLE_TRADING_SIGN:
        return {
          nextLabel: isLoading ? t("signing") : t("sign_message"),
          nextDisabled: isLoading,
        };
      case ConnectWalletPageEnum.DEPOSIT: {
        let nextLabel = t("deposit");

        if (chainId !== selectedDepositChain) {
          nextLabel = t("switch_to_chain", {
            chain: ID_TO_CHAINS[selectedDepositChain],
          });
        } else if (!depositReady) {
          if (getAssetPermitSignature) {
            nextLabel = t("permit", { asset: assetToDeposit });
          } else {
            nextLabel = t("approve", { asset: assetToDeposit });
          }
        }

        if (loadingAllowance) {
          nextLabel = t("loading");
        } else if (isLoading) {
          // eslint-disable-next-line no-nested-ternary
          nextLabel = depositReady
            ? t("depositing")
            : getAssetPermitSignature
            ? t("permitting", { asset: assetToDeposit })
            : t("approving", { asset: assetToDeposit });
        }

        // Check for errors
        const notEnoughEthError =
          chainId === selectedDepositChain &&
          selectedDepositChain !== supportedChainId &&
          ethBalance.lt(depositFees || 0);

        return {
          nextLabel,
          nextDisabled: loadingAllowance || isLoading || notEnoughEthError,
          info: {
            onInfoClick: () => {
              setShowHowDepositsWork(true);
            },
          },
        };
      }
      default:
        return {
          nextLabel: "",
          nextDisabled: true,
        };
    }
  }, [
    assetToDeposit,
    chainId,
    depositFees,
    depositReady,
    ethBalance,
    getAssetPermitSignature,
    isConnectingToWallet,
    isLoading,
    loadingAllowance,
    page,
    selectedDepositChain,
    selectedWallet,
    t,
    availablePages,
  ]);

  return {
    currentPageTitle,
    pageContent,
    stepInfo,
  };
}
